import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Sunrise, Flame, CalendarCheck, Trophy, CheckCircle,
  BookOpen, Crown, Award, Lock, Star
} from 'lucide-react';

const ICON_MAP = {
  'sunrise': Sunrise,
  'flame': Flame,
  'calendar-check': CalendarCheck,
  'trophy': Trophy,
  'check-circle': CheckCircle,
  'book-open': BookOpen,
  'crown': Crown,
  'award': Award,
};

const LEVEL_COLORS = {
  1: 'from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900',
  2: 'from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900',
  3: 'from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900',
  4: 'from-yellow-50 to-amber-100 dark:from-yellow-950 dark:to-amber-900',
};

export default function AchievementsPage() {
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/gamification');
      setGamification(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-muted rounded-2xl animate-pulse" />)}</div>;
  }

  if (!gamification) return null;

  const { badges, xp, level, level_num, next_xp, stats } = gamification;
  const xpProgress = next_xp > 0 ? Math.min((xp / next_xp) * 100, 100) : 100;
  const unlockedCount = badges.filter(b => b.unlocked).length;

  return (
    <div className="space-y-6" data-testid="achievements-page">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Conquistas</h1>
        <p className="text-muted-foreground text-sm mt-1">Celebre cada vitoria na sua jornada</p>
      </div>

      {/* Level Card */}
      <Card className={`bg-gradient-to-br ${LEVEL_COLORS[level_num] || LEVEL_COLORS[1]} border-none`} data-testid="level-card">
        <CardContent className="py-8 text-center">
          <div className="w-16 h-16 rounded-full bg-white/80 dark:bg-black/30 flex items-center justify-center mx-auto mb-3 shadow-lg">
            {level_num >= 4 ? <Crown className="w-8 h-8 text-yellow-500" /> :
             level_num >= 3 ? <Star className="w-8 h-8 text-purple-500" /> :
             level_num >= 2 ? <Award className="w-8 h-8 text-blue-500" /> :
             <Sunrise className="w-8 h-8 text-gray-500" />}
          </div>
          <Badge className="mb-2 text-sm px-4 py-1">{level}</Badge>
          <p className="text-4xl font-heading font-bold mt-2">{xp} XP</p>
          <div className="max-w-xs mx-auto mt-4">
            <Progress value={xpProgress} className="h-3" />
            <p className="text-xs text-muted-foreground mt-1">{xp} / {next_xp} XP para o proximo nivel</p>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-heading font-bold text-primary">{unlockedCount}</p>
            <p className="text-xs text-muted-foreground">Conquistas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-heading font-bold text-primary">{stats?.current_streak || 0}</p>
            <p className="text-xs text-muted-foreground">Dias Streak</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <p className="text-2xl font-heading font-bold text-primary">{stats?.total_habits_completed || 0}</p>
            <p className="text-xs text-muted-foreground">Habitos Feitos</p>
          </CardContent>
        </Card>
      </div>

      {/* XP Breakdown */}
      <Card data-testid="xp-breakdown">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Como ganhar XP</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Habito concluido</span>
              <Badge variant="secondary">+10 XP</Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Dia sem recaida</span>
              <Badge variant="secondary">+5 XP</Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Entrada no diario</span>
              <Badge variant="secondary">+15 XP</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badges */}
      <div>
        <h2 className="text-xl font-heading font-semibold mb-4">Medalhas ({unlockedCount}/{badges.length})</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 stagger-children">
          {badges.map((badge) => {
            const Icon = ICON_MAP[badge.icon] || Award;
            return (
              <Card
                key={badge.id}
                className={`text-center transition-all ${badge.unlocked ? 'hover:shadow-md' : 'opacity-50'}`}
                data-testid={`badge-${badge.id}`}
              >
                <CardContent className="py-5 px-3">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${
                    badge.unlocked
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {badge.unlocked ? <Icon className="w-7 h-7" /> : <Lock className="w-6 h-6" />}
                  </div>
                  <p className="text-sm font-semibold leading-tight">{badge.name}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 leading-tight">{badge.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Level System */}
      <Card data-testid="level-system">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Sistema de Niveis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { name: 'Iniciante', range: '0-7 dias', xpRange: '0-99 XP', num: 1 },
              { name: 'Comprometido', range: '8-30 dias', xpRange: '100-499 XP', num: 2 },
              { name: 'Disciplinado', range: '31-90 dias', xpRange: '500-1999 XP', num: 3 },
              { name: 'Mestre', range: '90+ dias', xpRange: '2000+ XP', num: 4 },
            ].map((l) => (
              <div key={l.num} className={`flex items-center gap-3 py-2 px-3 rounded-xl ${level_num >= l.num ? 'bg-primary/5' : 'bg-muted/30'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  level_num >= l.num ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {l.num}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${level_num >= l.num ? '' : 'text-muted-foreground'}`}>{l.name}</p>
                  <p className="text-[10px] text-muted-foreground">{l.range} | {l.xpRange}</p>
                </div>
                {level_num === l.num && <Badge className="text-[10px]">Atual</Badge>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
